import React, { useEffect, useMemo, useState } from 'react';
import { getIdToken, useAuth } from '@pooflabs/web';
import { FileInput, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { getPoofAPIUrl } from '@/lib/config';
import { getLoadedConfig, type EnvName } from '@/lib/poofClient';

type TierName = EnvName;

export type OneTimeIntegrationResult = {
  tier: TierName;
  appid: string;
  apiKey: string;
  webhookSecret: string;
  integration: Record<string, string>;
  integrationBlock: string;
  tenant?: Record<string, unknown>;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRegistered: (result: OneTimeIntegrationResult) => void;
};

type FormState = {
  tenantAppid: string;
  displayName: string;
  ownerWallet: string;
  recipientWallet: string;
  rails: string;
  paymentModel: string;
  backendUrl: string;
  webhookUrl: string;
  whopProductId: string;
};

const TIER_LABELS: Record<TierName, string> = {
  draft: 'Devnet/Draft',
  preview: 'mainnet-preview',
  production: 'production mainnet',
};

const INITIAL_FORM: FormState = {
  tenantAppid: '',
  displayName: '',
  ownerWallet: '',
  recipientWallet: '',
  rails: 'usdc,sol,usdt',
  paymentModel: 'one-time',
  backendUrl: '',
  webhookUrl: '',
  whopProductId: '',
};

function cleanLabel(label: string): string {
  return label
    .toLowerCase()
    .replace(/[`*_]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function manifestMap(text: string): Record<string, string> {
  const fields: Record<string, string> = {};
  for (const line of text.split(/\r?\n/)) {
    const match = line.match(/^\s*(?:[-*]\s*)?(?:\*\*)?([^:#]+?)(?:\*\*)?\s*:\s*(.+?)\s*$/);
    if (!match) continue;
    fields[cleanLabel(match[1])] = match[2].replace(/^`|`$/g, '').trim();
  }
  return fields;
}

function pick(fields: Record<string, string>, labels: string[]): string {
  for (const label of labels) {
    const value = fields[cleanLabel(label)];
    if (value) return value;
  }
  return '';
}

function deriveSlug(value: string): string {
  const trimmed = value.trim().replace(/\/+$/, '');
  return trimmed.split('/').pop() ?? trimmed;
}

function parseManifest(text: string, tier: TierName): Partial<FormState> {
  const fields = manifestMap(text);
  const tierPrefix = tier === 'draft' ? 'Draft' : tier === 'preview' ? 'Mainnet-preview' : 'Production';
  const productSlug = pick(fields, ['Product slug']);
  const tenantAppid = pick(fields, ['Tenant appid', 'Tenant app id', 'Appid']) || deriveSlug(productSlug);

  return {
    tenantAppid,
    displayName: pick(fields, ['Display name', 'Tenant name', 'Name']),
    ownerWallet: pick(fields, ['Owner wallet', 'Owner']),
    recipientWallet: pick(fields, ['Recipient wallet', 'Recipient', 'Default recipient wallet']),
    rails: pick(fields, ['Rails', 'Accepted rails', 'Accepts']),
    paymentModel: pick(fields, ['Payment model', 'Model']),
    backendUrl: pick(fields, [`${tierPrefix} backend URL`, 'Backend URL']),
    webhookUrl: pick(fields, [`${tierPrefix} webhook URL`, 'Webhook URL', 'Product webhook']),
    whopProductId: pick(fields, ['Whop product id', 'Whop product ID']),
  };
}

export const RegisterPoofyAppDialog: React.FC<Props> = ({ open, onOpenChange, onRegistered }) => {
  const { user } = useAuth();
  const [tier, setTier] = useState<TierName>('draft');
  const [manifestText, setManifestText] = useState('');
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user?.address && !form.ownerWallet) {
      setForm((current) => ({ ...current, ownerWallet: user.address }));
    }
  }, [form.ownerWallet, user?.address]);

  const tierApiUrl = useMemo(() => {
    const config = getLoadedConfig();
    const env = config?.environments?.[tier] ?? config?.[tier];
    return (env?.backendUrl ?? getPoofAPIUrl()).replace(/\/+$/, '');
  }, [tier]);

  const updateField = (field: keyof FormState) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const applyManifest = () => {
    const parsed = parseManifest(manifestText, tier);
    setForm((current) => ({
      ...current,
      ...Object.fromEntries(
        Object.entries(parsed).filter(([, value]) => typeof value === 'string' && value.trim().length > 0),
      ),
    }));
    toast.success('Manifest fields loaded');
  };

  const handleSubmit = async () => {
    if (!user?.address) {
      toast.error('Connect wallet before registering an app');
      return;
    }
    setSubmitting(true);
    try {
      const token = await getIdToken();
      const res = await fetch(`${tierApiUrl}/api/v1/tenants/register-poofy-app`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Wallet-Address': user.address,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tier,
          tenantAppid: form.tenantAppid.trim(),
          displayName: form.displayName.trim(),
          ownerWallet: form.ownerWallet.trim(),
          recipientWallet: form.recipientWallet.trim(),
          rails: form.rails.split(',').map((rail) => rail.trim()).filter(Boolean),
          paymentModel: form.paymentModel.trim(),
          backendUrl: form.backendUrl.trim(),
          webhookUrl: form.webhookUrl.trim(),
          whopProductId: form.whopProductId.trim() || undefined,
        }),
      });
      const json = await res.json().catch(() => null) as { success?: boolean; data?: OneTimeIntegrationResult; error?: { message?: string } } | null;
      if (!res.ok || !json?.success || !json.data) {
        toast.error(json?.error?.message ?? `Registration failed (${res.status})`);
        return;
      }
      onRegistered(json.data);
      toast.success('Poofy app registered');
      onOpenChange(false);
      setManifestText('');
      setForm(INITIAL_FORM);
    } catch {
      toast.error('Registration request failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid='register-poofy-app-dialog'
        className='max-h-[92vh] max-w-3xl overflow-y-auto bg-[var(--pp-surface)] text-[var(--pp-text)]'
      >
        <DialogHeader>
          <DialogTitle>Register Poofy app</DialogTitle>
          <DialogDescription>
            Select the Poofy Pay tier, paste a payment manifest, or enter the tenant fields directly.
          </DialogDescription>
        </DialogHeader>

        <div className='grid gap-5'>
          <div className='grid gap-2'>
            <Label>Tier</Label>
            <Select value={tier} onValueChange={(value) => setTier(value as TierName)}>
              <SelectTrigger className='bg-[var(--pp-subtle)]'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='draft'>{TIER_LABELS.draft}</SelectItem>
                <SelectItem value='preview'>{TIER_LABELS.preview}</SelectItem>
                <SelectItem value='production'>{TIER_LABELS.production}</SelectItem>
              </SelectContent>
            </Select>
            <p className='text-xs text-[var(--pp-text-subtle)]'>Poofy Pay API target: {tierApiUrl}</p>
          </div>

          <div className='grid gap-2'>
            <Label htmlFor='poofy-pay-manifest'>Payment manifest</Label>
            <Textarea
              id='poofy-pay-manifest'
              value={manifestText}
              onChange={(event) => setManifestText(event.target.value)}
              placeholder='Paste poof-artifacts.md ## Poofy Pay Manifest'
              className='min-h-32 bg-[var(--pp-subtle)] font-mono text-xs'
            />
            <Button type='button' variant='outline' className='w-fit gap-2' onClick={applyManifest}>
              <FileInput size={14} />
              Use manifest
            </Button>
          </div>

          <div className='grid gap-3 md:grid-cols-2'>
            <div className='grid gap-1.5'>
              <Label>Tenant appid</Label>
              <Input value={form.tenantAppid} onChange={updateField('tenantAppid')} />
            </div>
            <div className='grid gap-1.5'>
              <Label>Display name</Label>
              <Input value={form.displayName} onChange={updateField('displayName')} />
            </div>
            <div className='grid gap-1.5'>
              <Label>Owner wallet</Label>
              <Input value={form.ownerWallet} onChange={updateField('ownerWallet')} />
            </div>
            <div className='grid gap-1.5'>
              <Label>Recipient wallet</Label>
              <Input value={form.recipientWallet} onChange={updateField('recipientWallet')} />
            </div>
            <div className='grid gap-1.5'>
              <Label>Rails</Label>
              <Input value={form.rails} onChange={updateField('rails')} />
            </div>
            <div className='grid gap-1.5'>
              <Label>Payment model</Label>
              <Input value={form.paymentModel} onChange={updateField('paymentModel')} />
            </div>
            <div className='grid gap-1.5'>
              <Label>Consuming app backend URL</Label>
              <Input value={form.backendUrl} onChange={updateField('backendUrl')} />
            </div>
            <div className='grid gap-1.5'>
              <Label>Webhook URL</Label>
              <Input value={form.webhookUrl} onChange={updateField('webhookUrl')} />
            </div>
            <div className='grid gap-1.5 md:col-span-2'>
              <Label>Whop product id</Label>
              <Input value={form.whopProductId} onChange={updateField('whopProductId')} />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type='button' className='gap-2' onClick={handleSubmit} disabled={submitting}>
            <Send size={14} />
            {submitting ? 'Registering' : 'Register app'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RegisterPoofyAppDialog;
